// Contratos da área de Trackeamento: tag do site, verificação, eventos, landing pages e contatos.
import type { LandingContent } from '../landing'

/** Onde as pessoas chegam depois de clicar no anúncio. */
export const SITE_MODES = ['own_site', 'landing_page', 'whatsapp_only'] as const
export type SiteMode = (typeof SITE_MODES)[number]

/** Plataformas com passo a passo de instalação. */
export const SITE_PLATFORMS = ['wordpress', 'wix', 'shopify', 'nuvemshop', 'gtm', 'custom', 'other'] as const
export type SitePlatform = (typeof SITE_PLATFORMS)[number]

/** Eventos que a tag do Impulso registra. */
export const TRACK_EVENTS = ['page_view', 'whatsapp_click', 'phone_click', 'form_submit', 'lead', 'purchase'] as const
export type TrackEvent = (typeof TRACK_EVENTS)[number]

export const TRACK_EVENT_LABELS: Record<TrackEvent, string> = {
  page_view: 'Visitas',
  whatsapp_click: 'Cliques no WhatsApp',
  phone_click: 'Cliques para ligar',
  form_submit: 'Formulários enviados',
  lead: 'Contatos recebidos',
  purchase: 'Compras',
}

export interface TrackingSettings {
  /** ID do pixel da Meta (só números, 15-16 dígitos) */
  metaPixelId: string | null
  /** Tag do Google: AW-XXXXXXXXX (Google Ads) ou G-XXXXXXX (Analytics) */
  googleTagId: string | null
  /** Rótulo de conversão do Google Ads (parte depois da barra em send_to) */
  googleConversionLabel: string | null
  /** Rótulos por tipo de evento (contato = WhatsApp/ligação; lead = formulário; compra). Se faltar, vale o rótulo geral. */
  googleConversionLabels?: Partial<Record<'lead' | 'contact' | 'purchase', string | null>>
  /** Eventos automáticos */
  autoEvents: { whatsapp: boolean; phone: boolean; forms: boolean }
  /**
   * Envio dos contatos das landing pages também pelo servidor (API de Conversões da Meta e
   * importação de conversões do Google). Mais preciso que só o navegador (bloqueadores, iOS).
   */
  serverSide?: ServerSideSettings
}

export interface ServerSideSettings {
  /** Conexão Meta usada para enviar ao pixel (precisa ter acesso a ele) */
  metaConnectionId: string | null
  /** Conexão Google Ads e ação de conversão do tipo importação (UPLOAD_CLICKS) */
  googleConnectionId: string | null
  googleUploadActionId: string | null
}

/** Pixels e ações de conversão encontrados nas contas conectadas. */
export interface TrackingSourcesDto {
  items: Array<{
    connectionId: string
    provider: 'meta' | 'google_ads' | 'fake'
    accountName: string | null
    error: string | null
    pixels: Array<{ id: string; name: string; lastFiredAt: string | null }>
    conversionActions: Array<{
      id: string
      name: string
      /** webpage = tag no site; upload = importação de cliques (usada no envio pelo servidor) */
      kind: 'webpage' | 'upload' | 'other'
      tagId: string | null
      label: string | null
    }>
  }>
}

/** Resultado do envio de um contato às plataformas. */
export interface LeadForwardStatus { status: 'sent' | 'skipped' | 'error'; at: string; detail?: string | null }

export interface SiteCheckDto {
  checkedAt: string
  url: string
  ok: boolean
  error: string | null
  found: { impulsoTag: boolean; metaPixel: boolean; googleTag: boolean; gtm: boolean }
  detectedPlatform: SitePlatform | null
  ids: { metaPixelIds: string[]; googleTagIds: string[]; gtmIds: string[] }
}

export type TrackingStatus = 'not_started' | 'waiting' | 'receiving' | 'stale'

export interface TrackingChecklistItem {
  id: 'destination' | 'ids' | 'install' | 'signal' | 'conversion'
  title: string
  description: string
  done: boolean
  optional?: boolean
  /** Rota relativa à empresa (ex.: 'trackeamento/meu-site') */
  to: string
}

/** GET /companies/:companyId/tracking */
export interface TrackingSetupDto {
  publicKey: string
  /** URL do script da tag */
  tagUrl: string
  /** Código para colar no <head> do site */
  snippet: string
  siteMode: SiteMode | null
  platform: SitePlatform | null
  siteUrl: string | null
  settings: TrackingSettings
  status: TrackingStatus
  lastSeenAt: string | null
  lastSeenUrl: string | null
  lastCheck: SiteCheckDto | null
  checklist: TrackingChecklistItem[]
  /** Conexões de anúncio com problema de rastreamento informado pela plataforma */
  adAccounts: Array<{ provider: string; name: string | null; trackingOk: boolean | null; lastConversionEventAt: string | null }>
  landing: LandingAllowanceDto
}

/** PATCH /companies/:companyId/tracking */
export interface UpdateTrackingRequest {
  siteMode?: SiteMode | null
  platform?: SitePlatform | null
  siteUrl?: string | null
  settings?: Partial<Omit<TrackingSettings, 'autoEvents' | 'serverSide'>> & { autoEvents?: Partial<TrackingSettings['autoEvents']>; serverSide?: Partial<ServerSideSettings> }
}

/** GET /companies/:companyId/tracking/stats?days=7|30 */
export interface TrackingStatsDto {
  days: number
  totals: Record<TrackEvent, number>
  byDay: Array<{ day: string } & Record<TrackEvent, number>>
  bySource: Array<{ source: string; label: string; pageViews: number; conversions: number }>
}

// ---------------------------------------------------------------- landing pages

export interface LandingAllowanceDto {
  /** Add-on contratado (ou liberado pelo suporte) */
  active: boolean
  /** Páginas publicadas permitidas */
  maxPublished: number
  published: number
  /** Mensalidade do add-on em reais */
  price: number
  /** Quem vê pode ativar (permissão de cobrança na conta) */
  canManageBilling: boolean
  accountId: string
}

export type LandingStatus = 'draft' | 'published' | 'unpublished'

export interface LandingPageSummaryDto {
  id: string
  title: string
  slug: string
  status: LandingStatus
  url: string
  hasUnpublishedChanges: boolean
  publishedAt: string | null
  updatedAt: string
  views30d: number
  leads30d: number
}

export interface LandingPageDto extends LandingPageSummaryDto {
  content: LandingContent
}

export interface LandingListResponse { items: LandingPageSummaryDto[]; allowance: LandingAllowanceDto }

/** POST /companies/:companyId/landing-pages */
export interface CreateLandingRequest { title: string; template: LandingTemplateId }

/** PATCH /companies/:companyId/landing-pages/:id */
export interface UpdateLandingRequest { title?: string; slug?: string; content?: LandingContent }

export const LANDING_TEMPLATES = ['services', 'leads', 'offer', 'food', 'link_bio', 'blank'] as const
export type LandingTemplateId = (typeof LANDING_TEMPLATES)[number]

export interface LandingLeadDto {
  id: string
  pageId: string | null
  pageTitle: string | null
  name: string | null
  phone: string | null
  email: string | null
  message: string | null
  source: string | null
  status: 'new' | 'contacted'
  createdAt: string
  forwarded: { meta?: LeadForwardStatus; google?: LeadForwardStatus } | null
  /** A pessoa autorizou o uso dos dados em anúncios (marcou a caixa do formulário); null = formulário sem a pergunta */
  adsConsent: boolean | null
  /** Só verdadeiro quando a empresa registrou autorização explícita; nunca é inferido do canal de origem. */
  whatsappConsent: boolean | null
}
export interface LandingLeadsResponse { items: LandingLeadDto[] }

export type ManualLeadDuplicateDto = LandingLeadDto
export type CreateManualLeadResponse =
  | { status: 'created'; id: string }
  | { status: 'duplicates'; items: ManualLeadDuplicateDto[] }

export type LeadSaleKind = 'sale' | 'repurchase'
export interface LeadSaleDto {
  id: string
  kind: LeadSaleKind
  amountCents: number
  soldAt: string
  status: 'confirmed' | 'cancelled'
  cancelledAt: string | null
  createdAt: string
}
export interface LeadSalesResponse { items: LeadSaleDto[] }
export interface LeadSalesSourceSummaryItemDto { source: string | null; saleCount: number; amountCents: string }
export interface LeadSalesSourceSummaryDto {
  items: LeadSalesSourceSummaryItemDto[]
  totalSaleCount: number
  totalAmountCents: string
}
export interface CreateLeadSaleRequest { kind: LeadSaleKind; amountCents: number; soldAt: string }
export interface CreateManualLeadRequest { name?: string; phone: string; message?: string; whatsappConsent: boolean; allowSamePhone?: boolean }

// ---------------------------------------------------------------- domínio próprio

export type LandingDomainStatus = 'pending' | 'active' | 'failed'

export interface LandingDomainDto {
  id: string
  hostname: string
  status: LandingDomainStatus
  homePageId: string | null
  /** Registros DNS que a pessoa precisa criar */
  dns: { cname: { name: string; value: string }; txt: { name: string; value: string } }
  lastError: string | null
  lastCheckAt: string | null
  verifiedAt: string | null
  url: string
}
export interface LandingDomainsResponse { items: LandingDomainDto[]; max: number }
